// OBEyewear — cart drawer bundle logic
// 1. One-tap "+ Add" for the upsell rail (updates drawer, bag count and progress bar)
// 2. Auto-applies OBE25 once the bag has 2+ pairs (only if no other code is in the cart,
//    and never re-applies if the customer removes it)
// 3. Copy-code button
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

const CODE = 'OBE25';
const MIN_PAIRS = 2;
const SECTION_ID = 'cart-drawer-section';
const AUTO_FLAG = 'obe25-auto-applied';
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const postJSON = (path, body) =>
  fetch(`${ROOT}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => r.json());

const getCart = () => fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

const countPairs = (cart) =>
  (cart.items || [])
    .filter((i) => String(i.product_type || '').toLowerCase() === 'sunglasses')
    .reduce((n, i) => n + i.quantity, 0);

const hasAnyCode = (cart) =>
  (cart.discount_codes || []).length > 0 ||
  (cart.cart_level_discount_applications || []).some((a) => a.type === 'discount_code') ||
  (cart.items || []).some((i) =>
    (i.line_level_discount_allocations || []).some(
      (d) => d.discount_application && d.discount_application.type === 'discount_code'
    )
  );

const drawerOnPage = () => !!document.querySelector(`cart-items-component[data-section-id="${SECTION_ID}"]`);

let applying = false;

async function maybeApplyCode(cart) {
  if (applying || !cart || countPairs(cart) < MIN_PAIRS || hasAnyCode(cart)) return;
  try {
    if (sessionStorage.getItem(AUTO_FLAG)) return; // customer removed it — respect that
  } catch (e) {}

  applying = true;
  try {
    const res = await postJSON('cart/update.js', { discount: CODE, sections: SECTION_ID });
    try {
      sessionStorage.setItem(AUTO_FLAG, '1');
    } catch (e) {}
    const html = res && res.sections && res.sections[SECTION_ID];
    if (html && drawerOnPage()) morphSection(SECTION_ID, html, { mode: 'hydration' });
  } catch (err) {
    console.warn('[obe-bundle] could not apply code', err);
  } finally {
    applying = false;
  }
}

async function addVariant(link) {
  const id = Number(link.dataset.obeAdd);
  if (!id || link.classList.contains('is-loading')) return;
  link.classList.add('is-loading');

  const deferred = CartLinesUpdateEvent.createPromise();
  link.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(id), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await postJSON('cart/add.js', { items: [{ id, quantity: 1 }], sections: SECTION_ID });
    if (res.status) throw new Error(res.description || res.message || 'Add to bag failed');
    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: 1,
        source: 'obe-upsell',
        sections: res.sections,
        didError: false,
      },
    });
  } catch (err) {
    console.warn('[obe-bundle] add failed', err);
    deferred.reject(err);
    link.classList.remove('is-loading');
    link.textContent = 'Unavailable';
  }
}

document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  if (!(target instanceof Element)) return;

  const add = target.closest('[data-obe-add]');
  if (add) {
    event.preventDefault();
    addVariant(add);
    return;
  }

  const copy = target.closest('[data-obe-copy]');
  if (copy) {
    navigator.clipboard?.writeText(copy.dataset.obeCopy || CODE);
    copy.textContent = 'Copied';
    setTimeout(() => (copy.textContent = 'Copy'), 2000);
  }
});

// Any cart change (product page add, quantity +/-, upsell add) → check whether OBE25 should go on
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ detail } = {}) => {
      if (detail && detail.didError) return;
      return getCart().then(maybeApplyCode);
    })
    .catch(() => {});
});

// On page load, in case the bag already qualifies
getCart().then(maybeApplyCode).catch(() => {});
